import React from 'react'
import { Typography, IconButton } from '@mui/material'
import ShareIcon from '@mui/icons-material/Share'
import { Activity, OccationInstance } from '../../../pure-js/types/GrooverTypes'
import { format } from 'date-fns'
import { enUS, nb, sv } from 'date-fns/locale'
import Box from './Box'
import useAppState from '../hooks/useAppState'
import { formatGrooverText, getGrooverText } from '../libs/GrooverTextRepository'
import { resolveActivityEndTime } from '../../../pure-js/libs/ActivityTimeHelper'

interface RegistrationOccurrencesProps {
  activity: Activity
}

const RegistrationOccurrences: React.FC<RegistrationOccurrencesProps> = ({ activity }) => {
  const { state } = useAppState()
  const language = state.user?.language

  const generateOccations = (): OccationInstance[] => {
    if (activity.occations && activity.occations.length > 0) {
      // Sort provided occations by startDate
      return activity.occations
        .slice()
        .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
    }

    const start = new Date(activity.startDate || '')
    const generatedOccations: OccationInstance[] = []

    if (!activity.noOfOccasions) return generatedOccations

    for (let i = 0; i < activity.noOfOccasions; i++) {
      const occationStart = new Date(start)
      occationStart.setDate(start.getDate() + i * 7) // Weekly intervals

      if (!activity.time) return generatedOccations
      const [hours, minutes] = activity.time.split(':').map(Number)
      occationStart.setHours(hours, minutes, 0, 0)

      const resolvedEndTime = resolveActivityEndTime(activity)
      const occationEnd = new Date(occationStart)
      if (resolvedEndTime) {
        const [endHours, endMinutes] = resolvedEndTime.split(':').map(Number)
        occationEnd.setHours(endHours, endMinutes, 0, 0)
        // End time at/before start means the activity crosses midnight
        if (occationEnd.getTime() <= occationStart.getTime()) {
          occationEnd.setDate(occationEnd.getDate() + 1)
        }
      }

      generatedOccations.push({
        id: `occasion-id-${i}`,
        startDate: occationStart.toISOString(),
        endDate: occationEnd.toISOString(),
        instructors: [],
        index: i + 1
      })
    }

    return generatedOccations
  }

  const displayOccations = generateOccations()
  const locale = language === 'no' ? nb : language === 'en' ? enUS : sv

  const normalize = (s: string) =>
    s.replace(/\b[a-zåäöæø]+\b/g, (w) => w.charAt(0).toUpperCase() + w.slice(1)).replace(/\./g, '')

  const pluralizeWeekday = (name: string): string => {
    if (language === 'no') return name.replace(/dag$/i, 'dager')
    if (language === 'en') return `${name}s`
    return name.replace(/dag$/i, 'dagar') // sv default
  }

  const getHeader = (): string => {
    // Case 1: single occasion — show the date/time directly
    if (displayOccations.length === 1) {
      return normalize(format(new Date(displayOccations[0].startDate), 'EEEE d/M HH:mm', { locale }))
    }

    const count = formatGrooverText(getGrooverText('userWeb.activityDetail.occurrencesHeader', language), [
      activity.noOfOccasions || displayOccations.length
    ])

    // Map JS getDay() (0=Sun) to ISO weekday (1=Mon … 7=Sun) for European sort order
    const toIsoDay = (d: number) => (d === 0 ? 7 : d)

    const seen = new Map<number, Date>()
    displayOccations.forEach((occ) => {
      const date = new Date(occ.startDate)
      const day = toIsoDay(date.getDay())
      if (!seen.has(day)) seen.set(day, date)
    })

    const sortedEntries = Array.from(seen.entries()).sort(([a], [b]) => a - b)

    // Case 2: multiple occasions, all on the same weekday — plural full day name
    if (sortedEntries.length === 1) {
      const fullDay = normalize(format(sortedEntries[0][1], 'EEEE', { locale }))
      return `${count} - ${pluralizeWeekday(fullDay)}`
    }

    // Case 3: multiple occasions, different weekdays — abbreviated, joined with &
    const abbreviated = sortedEntries.map(([, date]) => normalize(format(date, 'EEE', { locale })))
    return `${count} - ${abbreviated.join(' & ')}`
  }

  return (
    <div className="segment-container">
      <div className="header">
        <Box direction="row" justify="space-between" fullWidth>
          <Typography variant="h5" component="h2" className="header-title">
            {getHeader()}
          </Typography>
        </Box>
      </div>
      <Box direction="column" gap="8px" fullWidth>
        {displayOccations.map((occation, index) => (
          <Box key={index} direction="row" align="center" style={{ padding: '4px 0' }}>
            <Typography variant="body2" style={{ fontSize: '14px', lineHeight: 1.3, color: '#333' }}>
              {(() => {
                const start = new Date(occation.startDate)
                const startStr = format(start, 'EEEE d/M HH:mm', { locale })
                  .replace(/\b[a-zåäöæø]+\b/g, (word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .replace(/\./g, '')

                let endStr: string | null = null
                if (occation.endDate) {
                  endStr = format(new Date(occation.endDate), 'HH:mm', { locale })
                } else {
                  endStr = resolveActivityEndTime(activity) ?? null
                }

                return endStr ? `${startStr}–${endStr}` : startStr
              })()}
            </Typography>
          </Box>
        ))}
      </Box>
    </div>
  )
}

export default RegistrationOccurrences
